import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

type Props = {
  selectedEmployeeIds: string[]
  eligibleEmployeeIds?: string[]
  terminatedSelectedCount?: number
  onCancel: () => void
  onConfirm: (employeeIds: string[]) => void
}

const BulkTerminateModal = ({
  selectedEmployeeIds,
  eligibleEmployeeIds,
  terminatedSelectedCount,
  onCancel,
  onConfirm,
}: Props) => {
  const { company } = useCurrentSession()
  const selectedCount = selectedEmployeeIds.length
  const eligibleIds = eligibleEmployeeIds || selectedEmployeeIds
  const terminatedCount =
    typeof terminatedSelectedCount === 'number'
      ? terminatedSelectedCount
      : Math.max(selectedCount - (eligibleEmployeeIds?.length || selectedEmployeeIds.length), 0)

  return (
    <Modal
      title={
        terminatedCount > 0
          ? i18n.t('employees.termination.bulk_modal.some_terminated_title', {
              defaultValue: 'Some of the selected employees are already terminated',
            })
          : i18n.t('employees.termination.bulk_modal.title', {
              employees: selectedCount,
              defaultValue: `Terminate ${selectedCount} employees`,
            })
      }
      description={
        terminatedCount > 0 ? (
          <Box gap="s20">
            <Text.Normal>
              {i18n.t('employees.termination.bulk_modal.some_terminated_description_top', {
                selected: selectedCount,
                terminated: terminatedCount,
                defaultValue:
                  'Of the %{selected} selected employees, %{terminated} are currently terminated or with a future termination date. Click ‘Continue’ to proceed with the eligible employees.',
              })}
            </Text.Normal>

            <Text.Normal>
              {i18n.t('employees.termination.bulk_modal.description', {
                defaultValue:
                  'Access to Factorial will be revoked from the selected employee(s). The agreement will remain active until the end date.',
              })}
            </Text.Normal>
          </Box>
        ) : (
          i18n.t('employees.termination.bulk_modal.description', {
            defaultValue:
              'Access to Factorial will be revoked from the selected employee(s). The agreement will remain active until the end date.',
          })
        )
      }
      illustration="document-edit"
      onClose={onCancel}
      footer={
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          <F0Button
            variant="outline"
            size="md"
            label={i18n.t('common.cancel')}
            onClick={onCancel}
          />
          <F0Button
            critical
            size="md"
            disabled={eligibleIds.length === 0}
            label={i18n.t('common.continue')}
            onClick={() => {
              // Track Bulk Termination Submit
              track(trackingEvents.bulkTermination.submit, {
                company_id: company.id,
                selected_count: selectedEmployeeIds.length,
                submitted_count: eligibleIds.length,
                already_terminated_count: terminatedCount,
              })

              onConfirm(eligibleIds)
            }}
          />
        </Box>
      }
    ></Modal>
  )
}

export default BulkTerminateModal
